import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link, useLocation } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import { pushBreadcrumb } from "../../store/slices/navigationSlice";
import {
  Alert,
  Spin,
  Tabs,
  Badge,
  Typography,
  Button,
  Table,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PreviewOrchestrator from "../preview/PreviewOrchestrator";
import {
  renderGrid,
  setByPathCloned,
  buildMinimalPatch,
} from "../common/FieldRenderer";
import {
  PP_PICKLIST_LABELS,
  buildInfoFieldsForProjectPage,
  buildSysFieldsForProjectPage,
} from "./projectPageColumnsConfig";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_PAGE } from "../../config/sobjectNames";
import {
  ChevronDown,
  ChevronRight,
  Edit,
  Eye,
  EyeOff,
  Zap,
  Save,
} from "lucide-react";
import { useRecents } from "../../hooks/useRecents";
import ProjectPageComponentsSection from "../sections/ProjectPageComponentsSection";
import { PPC_TYPE_TO_ESM } from "../project-page-component/ProjectPageComponentDetails";
import { mapProjectPageComponentToProps } from "./ProjectPageComponentMapper";

const PROJECT_PAGE_TYPE_TO_ESM = {
  "Project Active Page": "ProjectActivePage",
  "Project Qualifying Page": "ProjectQualifyingPage",
  "Project Pre-Qualification": "ProjectPreQualificationPage",
  "Project Splash Page (Preapply)": "SplashPage",
  "Default Qualification Page": "DefaultQualificationPage",
};

const SUB_TYPES = [
  {
    label: "Project Card",
    value: "ProjectCard",
  },
  {
    label: "Project Card - Invite",
    value: "ProjectCardInvite",
  },
  {
    label: "Banner",
    value: "Banner",
  },
];

function mapPpcToProps(esmName, data) {
  const baseProps = {};
  switch (esmName) {
    case "TwoColumn5050":
      baseProps.leftTitle = data.Headline_1__c || "";
      baseProps.leftContent = data.Text_Area_1__c || "";
      baseProps.leftButtonName = data.Button_Name_1__c || undefined;
      baseProps.leftCtaUrl =
        data.Button_URL_1__c || data.File_URL_1__c || undefined;
      baseProps.leftMediaUrl = data.Image_Location_1__c || undefined;
      baseProps.leftButtonType = data.Button_Type_1__c || undefined;
      baseProps.rightTitle = data.Headline_2__c || "";
      baseProps.rightContent = data.Text_Area_2__c || "";
      baseProps.rightButtonName = data.Button_Name_2__c || undefined;
      baseProps.rightCtaUrl =
        data.Button_URL_2__c || data.File_URL_2__c || undefined;
      baseProps.rightMediaUrl = data.Image_Location_2__c || undefined;
      baseProps.rightButtonType = data.Button_Type_2__c || undefined;
      baseProps.onButtonClick = (url) => {
        if (data.Button_Type_2__c === "Direct URL") {
          window.open(url, "_blank");
        } else if (data.Button_Type_2__c === "File Download") {
          window.open(url, "_blank");
        } else if (data.Button_Type_2__c === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "TwoColumnImageLeft5050":
      baseProps.leftTitle = data.Headline_1__c || "";
      baseProps.leftContent = data.Text_Area_1__c || "";
      baseProps.leftButtonName = data.Button_Name_1__c || "";
      baseProps.leftCtaUrl = data.Button_URL_1__c || data.File_URL_1__c || "";
      baseProps.leftMediaUrl = data.Image_Location_1__c || "";
      baseProps.leftButtonType = data.Button_Type_1__c || "";
      baseProps.rightTitle = data.Headline_2__c || "";
      baseProps.rightContent = data.Text_Area_2__c || "";
      baseProps.rightButtonName = data.Button_Name_2__c || "";
      baseProps.rightCtaUrl = data.Button_URL_2__c || data.File_URL_2__c || "";
      baseProps.rightMediaUrl = data.Image_Location_2__c || "";
      baseProps.rightButtonType = data.Button_Type_2__c || "";
      break;
    case "TwoColumnImageRight5050":
      baseProps.leftTitle = data.Headline_1__c || "";
      baseProps.leftContent = data.Text_Area_1__c || "";
      baseProps.leftButtonName = data.Button_Name_1__c || "";
      baseProps.leftCtaUrl = data.Button_URL_1__c || data.File_URL_1__c || "";
      baseProps.leftMediaUrl = data.Image_Location_1__c || "";
      baseProps.leftButtonType = data.Button_Type_1__c || "";
      baseProps.rightTitle = data.Headline_2__c || "";
      baseProps.rightContent = data.Text_Area_2__c || "";
      baseProps.rightButtonName = data.Button_Name_2__c || "";
      baseProps.rightCtaUrl = data.Button_URL_2__c || data.File_URL_2__c || "";
      baseProps.rightMediaUrl = data.Image_Location_2__c || "";
      baseProps.rightButtonType = data.Button_Type_2__c || "";
      break;
    case "TwoColumnImageLeft3366":
      baseProps.leftTitle = data.Headline_1__c || "";
      baseProps.leftContent = data.Text_Area_1__c || "";
      baseProps.leftButtonName = data.Button_Name_1__c || "";
      baseProps.leftCtaUrl = data.Button_URL_1__c || data.File_URL_1__c || "";
      baseProps.leftMediaUrl = data.Image_Location_1__c || "";
      baseProps.leftButtonType = data.Button_Type_1__c || "";
      baseProps.rightTitle = data.Headline_2__c || "";
      baseProps.rightContent = data.Text_Area_2__c || "";
      baseProps.rightButtonName = data.Button_Name_2__c || "";
      baseProps.rightCtaUrl = data.Button_URL_2__c || data.File_URL_2__c || "";
      baseProps.rightMediaUrl = data.Image_Location_2__c || "";
      baseProps.rightButtonType = data.Button_Type_2__c || "";
      break;
    case "TwoColumnImageRight3366":
      baseProps.title = data.Headline_1__c || "";
      baseProps.content = data.Text_Area_1__c || "";
      baseProps.buttonName = data.Button_Name_1__c || undefined;
      baseProps.ctaUrl =
        data.Button_URL_1__c || data.File_URL_1__c || undefined;
      baseProps.mediaUrl = data.Image_Location_2__c || undefined;
      baseProps.buttonType = data.Button_Type_1__c || undefined;
      baseProps.onButtonClick = (url) => {
        if (data.Button_Type_1__c === "Direct URL") {
          window.open(url, "_blank");
        } else if (data.Button_Type_1__c === "File Download") {
          window.open(url, "_blank");
        } else if (data.Button_Type_1__c === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "HeadlineOnly":
      baseProps.title = data.Headline_1__c || "";
      break;
    case "TextOnlyFullPage":
      baseProps.content = data.Text_Area_1__c || "";
      break;
    case "StandaloneButton":
      baseProps.buttonName = data.Button_Name_1__c || "";
      baseProps.ctaUrl = data.Button_URL_1__c || data.File_URL_1__c || "";
      baseProps.onButtonClick = (url) => {
        if (data.Button_Type_1__c === "Direct URL") {
          window.open(url, "_blank");
        } else if (data.Button_Type_1__c === "File Download") {
          window.open(url, "_blank");
        } else if (data.Button_Type_1__c === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "ImageOnly":
      baseProps.mediaUrl = data.Image_Location_1__c || "";
      break;
    default:
      console.log("Unknown Map Ppc to Props: ", esmName);
      break;
  }
  return baseProps;
}

export default function ProjectPageDetails() {
  const { projectPage, http, projectPageComponent } = useServices();
  const { projectPageId } = useParams();
  const dispatch = useDispatch();
  const breadcrumbs = useSelector((state) => state.navigation.breadcrumbs);
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [previewWidth, setPreviewWidth] = useState(50); // Percentage of available width
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const recordedIdRef = React.useRef(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [hideValidationSuccess, setHideValidationSuccess] = useState(false);
  const [payload, setPayload] = useState("N/A");
  const [esmName, setEsmName] = useState("ProjectPreQualificationPage");
  const [subType, setSubType] = useState(null);
  const [ppcs, setPpcs] = useState([]);

  // Handle preview resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left - 20;
      const newWidth = Math.min(
        Math.max((mouseX / containerWidth) * 100, 20),
        66.67,
      ); // Max 2/3 (66.67%)

      setPreviewWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!projectPageId) return;
      setData(null);
      setLoading(true);
      setError("");
      try {
        const record = await projectPage.fetchProjectPage(projectPageId);
        if (!ignore) setData(record);
      } catch (e) {
        if (!ignore) setError(e?.message || "Failed to load project page");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [projectPageId, projectPage]);

  useEffect(() => {
    // const esmName = PROJECT_PAGE_TYPE_TO_ESM[form?.info?.type || ""];
    // esmName && setEsmName(esmName);
    updateESMPreview();
  }, [form?.info?.type]);

  // Push breadcrumb when Project Page loads
  useEffect(() => {
    const id = data?.id || projectPageId;
    const provided = location?.state?.crumbLabel;
    const fallbackName = data?.info?.name;
    const name = provided || fallbackName;
    if (id && name) {
      if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        dispatch(pushBreadcrumb({ label: "Home", path: "/", type: "home" }));
      }
      dispatch(
        pushBreadcrumb({
          id,
          label: name || `Project Page ${id}`,
          path: `/pp/${id}`,
          type: "projectPage",
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.id,
    data?.info?.name,
    projectPageId,
    location.pathname,
    breadcrumbs?.length,
    location?.state?.crumbLabel,
  ]);

  // Record in Recently Viewed when opened
  useEffect(() => {
    const id = data?.id || projectPageId;
    if (data == null || !id || recordedIdRef.current === id) return;
    const name = data?.info?.name || "Project Page";
    record({
      id: String(id),
      title: String(name || id),
      url: `/pp/${id}`,
      type: "Project Page",
    });
    recordedIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, projectPageId]);

  const sys = data?.sys || {};

  useEffect(() => {
    if (isEditing) {
      setForm(data);
      setSuccess("");
    }
  }, [isEditing, data]);

  // Fetch PPCs only when projectPageId changes
  useEffect(() => {
    if (!projectPageId) {
      setPpcs([]);
      return;
    }

    let ignore = false;
    async function loadPPCs() {
      console.log("fetchPPCs since id changed", projectPageId);
      try {
        const comps =
          await projectPageComponent.fetchProjectPageComponentsByProjectPageId(
            projectPageId,
          );
        if (!ignore) {
          setPpcs(Array.isArray(comps) ? comps : []);
        }
      } catch (error) {
        console.error("Error fetching PPCs:", error);
        if (!ignore) {
          setPpcs([]);
        }
      }
    }
    loadPPCs();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPageId]);

  // Fetch PPCs when clicked on preview
  useEffect(() => {
    updateESMPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, ppcs]);

  useEffect(() => {
    console.log("subType", subType);
    updateESMPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subType, ppcs]);

  const updateESMPreview = async () => {
    if (!showPreview) return;

    let current_type =
      PROJECT_PAGE_TYPE_TO_ESM[(form || data)?.info?.type || ""];
    console.log("current_type", current_type, (form || data)?.info?.type);

    let pageProps = { projectPage: {} };
    let cardProps = {};

    if (current_type === "SplashPage") {
      console.log("current_type is SplashPage", subType);
      if (!subType) {
        // Prepare props based on subType
        if (subType === "ProjectCard") {
          cardProps = {
            project: {
              projectId: "123",
              projectName: "Project 1",
              projectDescription: "Project 1 description",
              projectImage: "https://via.placeholder.com/150",
            },
          };
        }

        setSubType(SUB_TYPES[0]?.value);
      }
    } else {
      console.log("subType to null", subType);
      subType && setSubType(null);
    }

    pageProps.projectPage = {
      ...cardProps,
      categories: ["Project Match/Active", "Spain", "Project Type"],
      title: "Self Reported Per Hour",
      splash_image_url:
        "https://mercury-ce.s3-us-west-2.amazonaws.com/a1kVC00000KwAczYAF/a0KVC000008h1h02AA/Image_Location_1__c/images__2__copy.jpeg?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20251115T162046Z&X-Amz-SignedHeaders=host&X-Amz-Expires=3600&X-Amz-Credential=AKIATGRM7ZPSMZAODWWR%2F20251115%2Fus-west-2%2Fs3%2Faws4_request&X-Amz-Signature=1d790e1b37331050e377fed1d586505dde30472f4be5ed8465f97db87f42f848",
      pay_rate: "$10.00 / Hour",
      productivity_target_type: "Hours",
      date_end: "2034-11-30",
      duration: "3301 days",
      hours_required: null,
      status: null,
    };

    // Use stored PPCs instead of fetching
    const rows = Array.isArray(ppcs) ? ppcs : [];
    const rowsOrdered = (Array.isArray(rows) ? rows : [])
      .slice()
      .sort((a, b) => {
        const ao = a.Component_Order__c ?? "";
        const bo = b.Component_Order__c ?? "";
        return String(ao).localeCompare(String(bo), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });

    console.log("rowsOrdered", rowsOrdered);

    // PPCs
    const components = rowsOrdered.map((p) => {
      const esmName = PPC_TYPE_TO_ESM[p?.RecordType?.Name || ""];
      const props = mapProjectPageComponentToProps(p);
      return props;
    });
    pageProps.projectPage.components = components;
    console.log("payload", pageProps);
    console.log("esmName", current_type);

    setPayload(pageProps);
    setEsmName(current_type);
  };

  const { picklistMap } = usePicklistMap(
    Object.values(PP_PICKLIST_LABELS),
    SOBJECT_PROJECT_PAGE,
  );

  function handleChange(path, value) {
    console.log("handleChange", path, value);
    setForm((prev) => setByPathCloned(prev || {}, path, value));
  }

  const validationGroups = useMemo(() => {
    const alerts = Array.isArray(validation?.alerts) ? validation.alerts : [];
    const groups = { error: [], warning: [], info: [] };
    for (const a of alerts) {
      const t = (a?.type === "warn" ? "warning" : a?.type) || "info";
      if (t === "error" || t === "warning" || t === "info") {
        groups[t].push(a);
      } else {
        groups.info.push(a);
      }
    }
    return groups;
  }, [validation]);

  async function handleValidate() {
    const id = data?.id || projectPageId;
    if (!id) {
      setValidationError("Missing record id");
      return;
    }
    setValidationError("");
    setValidating(true);
    setSuccess("");
    setHideValidationSuccess(false);
    try {
      const resp = await http.sfGet(`/Validate`, {
        id: String(id),
        type: "ProjectPage",
      });
      if (resp == null) {
        setValidation(null);
        return;
      }
      const successFlag = resp?.success === true;
      const alerts = Array.isArray(resp?.alerts) ? resp.alerts : [];
      setValidation({ success: successFlag, alerts });
    } catch (e) {
      const details = e?.details;
      if (details && typeof details === "object") {
        const alerts = Array.isArray(details.alerts) ? details.alerts : [];
        setValidation({ success: !!details.success, alerts });
      } else {
        setValidation(null);
        setValidationError(e?.message || "Validation failed");
      }
    } finally {
      setValidating(false);
    }
  }

  async function handleSave() {
    if (!form) return;
    const id = data?.id || projectPageId;
    if (!id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const partial = buildMinimalPatch(data || {}, form || {}) || {};
      if (Object.keys(partial).length === 0) {
        setIsEditing(false);
        setSuccess("No changes to update");
        return;
      }
      await projectPage.patchProjectPage(String(id), partial);
      const fresh = await projectPage.fetchProjectPage(String(id));
      setData(fresh);
      setIsEditing(false);
      setForm(null);
      setSuccess("Project Page updated");
    } catch (e) {
      setError(e?.message || "Failed to update project page");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sticky Header with Title and Action Buttons */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 rounded-lg p-4 mb-6 shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <div className="flex items-center space-x-2">
                  <Typography.Title
                    level={5}
                    style={{ margin: 0, color: "#6b7280" }}
                  >
                    Project Page
                  </Typography.Title>
                </div>
                <Typography.Title level={2} style={{ margin: 0, marginTop: 4 }}>
                  {data?.info?.name || "Unnamed Page"}
                </Typography.Title>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <Button
                  type="primary"
                  onClick={() => setIsEditing(true)}
                  icon={<Edit size={16} />}
                  className="shadow-sm"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      setForm(null);
                    }}
                    className="shadow-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={saving}
                    loading={saving}
                    className="shadow-sm"
                    icon={<Save size={16} />}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}

              <div className="h-6 w-px bg-gray-300"></div>

              <Button
                type="default"
                onClick={handleValidate}
                loading={validating}
                className="shadow-sm"
              >
                {validating ? "Validating..." : "Validate"}
              </Button>

              <Button
                type={showPreview ? "primary" : "default"}
                onClick={() => setShowPreview((v) => !v)}
                icon={showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                className="shadow-sm"
              >
                {showPreview ? "Hide Preview" : "Show Preview"}
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mb-6">
            <Spin fullscreen tip="Loading project page…" />
          </div>
        )}
        {error && (
          <div className="mb-6">
            <Alert type="error" showIcon message={error} />
          </div>
        )}
        {success && (
          <div className="mb-6">
            <Alert type="success" showIcon message={success} />
          </div>
        )}
        {validationError && (
          <div className="mb-6">
            <Alert type="error" showIcon message={validationError} />
          </div>
        )}
        {validation && (
          <div className="mb-6 space-y-4">
            {Array.isArray(validation.alerts) &&
            validation.alerts.length === 0 ? (
              !hideValidationSuccess ? (
                <div
                  className="fade-out-after-3s"
                  onAnimationEnd={() => setHideValidationSuccess(true)}
                >
                  <Alert
                    type="success"
                    showIcon
                    message="Validation Successful"
                    className="border-green-200 bg-green-50"
                  />
                </div>
              ) : null
            ) : (
              <div className="space-y-4">
                {validationGroups.error.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Validation Errors (${validationGroups.error.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.error.map((a, idx) => (
                          <li key={`err-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-red-200 bg-red-50"
                  />
                )}
                {validationGroups.warning.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`Warnings (${validationGroups.warning.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.warning.map((a, idx) => (
                          <li key={`warn-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-yellow-200 bg-yellow-50"
                  />
                )}
                {validationGroups.info.length > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`Information (${validationGroups.info.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.info.map((a, idx) => (
                          <li key={`info-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-blue-200 bg-blue-50"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {data && (
          <div
            ref={containerRef}
            className={`${showPreview ? "flex gap-4" : "max-w-6xl mx-auto"} ${
              isDragging ? "select-none cursor-col-resize" : ""
            }`}
          >
            <div
              className={`${
                showPreview ? `flex-shrink-0` : ""
              } h-min bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/20`}
              style={{
                width: showPreview ? `${previewWidth}%` : "100%",
              }}
            >
              <div className="p-6">
                <Tabs
                  defaultActiveKey="info"
                  items={[
                    {
                      key: "info",
                      label: "Info",
                      children: renderGrid(
                        resolvePicklistFields(
                          [
                            {
                              label: "Salesforce Id",
                              value: data?.id || "—",
                              readOnly: true,
                            },
                            ...buildInfoFieldsForProjectPage({
                              isEditing,
                              form,
                              data,
                            }),
                          ],
                          picklistMap,
                        ),
                        {
                          editing: isEditing,
                          form: form || data,
                          onChange: handleChange,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: data,
                          searchParams: {
                            projectId: (form || data)?.info?.projectId,
                            projectObjectiveId: (form || data)?.info
                              ?.projectObjectiveId,
                          },
                        },
                      ),
                    },
                    {
                      key: "sys",
                      label: "System",
                      children: renderGrid(buildSysFieldsForProjectPage(), {
                        editing: false,
                        form: form || data,
                        onEnableEdit: null, // System fields are not editable
                      }),
                    },
                  ]}
                />
                <ProjectPageComponentsSection
                  projectPageId={projectPageId}
                  projectPageName={data?.info?.name}
                />
              </div>
            </div>
            {showPreview && (
              <>
                {/* Drag Handle */}
                <div
                  className="w-2 cursor-col-resize flex-shrink-0 transition-colors duration-200 relative group"
                  onMouseDown={() => setIsDragging(true)}
                >
                  <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bg-gray-300 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-6 rounded-full group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                <div
                  className="flex-shrink-0"
                  style={{ width: `calc(${100 - previewWidth}% - 40px)` }}
                >
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Live Preview
                      </h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Zap size={16} className="mr-2" />
                        Real-time rendering
                      </div>
                    </div>
                    {subType && (
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-sm">Preview As:</span>
                        <div className="rounded-lg">
                          <Select
                            className="w-48"
                            options={SUB_TYPES}
                            value={subType}
                            defaultValue={SUB_TYPES[0].value}
                            onChange={(value) => setSubType(value)}
                          />
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg">
                      <PreviewOrchestrator
                        objectType={subType || esmName}
                        payload={payload}
                        id={data?.id}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
